/**
 * Application Routes Configuration
 * Centralized location for all route paths used throughout the application.
 */

export const AppRoutes = {
  root: "/",
  notFound: "*",

  thankYou: "/ThankYouSuccessMsg",
  comingSoon: "/ComingSoon",
  test: "/Test",
  logs: "/Logs",

  inbox: "/inbox",
  insureds: "/Insureds",
  correctedDocumentResubmission: "/CorrectedDocumentResubmission",
  document: "/Document",
  lateFilingForm: "/LateFilingForm",
  licenseMatrix: "/LicenseMatrix",
  fmFlagReview: "/FMFlagReview",
  newSubmission: "/NewSubmission",
  stateLogins: "/StateLogins",
  submissions: "/Submissions",
  transaction: "/Transaction",

  // authentication routes
  login: "/Login",
  resetPassword: "/resetPassword",
  forgotPassword: "/resetPassword/forgot",

  /** MISC-side My Notifications (ClientInbox feed for tag/signature client activity). */
  misc: {
    notifications: "/misc/notifications",
  },

  home: {
    base: "/home",
    diligentSearchLibrary: "/home/DiligentSearchLibrary",
    iWantTo: "/home/IWantTo",
    reportTag: "/home/ReportTag",
    requestSignature: "/home/RequestSignature",
    sendStampedDocument: "/home/SendStampedDocument",
    surplusLinesTaxEstimator: "/home/SurplusLinesTaxEstimator",
    createDiligentSearchForm: "/home/CreateDiligentSearchForm",
  },

  admin: {
    base: "/Admin",
    users: "/Admin/Users",
    clientUsers: "/Admin/ClientUsers",
    carrierManagement: "/Admin/CarrierManagement",
    insuranceCompany: "/Admin/InsuranceCompany",
    lineOfBusiness: "/Admin/LineOfBusiness",
    filingSchedule: "/Admin/Filing-Schedule",
    messageTemplates: "/Admin/Message-Templates",
    policyFilingSchedule: "/Admin/Policy-Filing-Schedule",
    manualStateSubmissionSchedule: "/Admin/ManualStateSubmissionSchedule",
    submissionData: "/Admin/SubmissionData",
    editCASLaSubmissionData: "/Admin/EditCASLaSubmissionData",
    stateStampList: "/Admin/StateStampList",
    stateLogins: "/Admin/StateLogins",
    statesZeroFilingRule: "/Admin/StatesZeroFilingRule",
    taxesAndFees: "/Admin/TaxesAndFees",
    partnerTaxAndFees: "/Admin/PartnerTaxandFees",
    taxScheduleByJurisdiction: "/Admin/TaxScheduleByJurisdiction",
    reporting: "/Admin/Reporting",
    reportModule: "/Admin/ReportModule",
    sendClientPortalUpdate: "/Admin/SendClientPortalUpdate",
    clientFeeSchedule: "/Admin/ClientFeeSchedule",
    addEditCustomerPolicyDetails: "/Admin/AddEditCustomerPolicyDetails",
  },

  billing: {
    monthEndBillingModule: "/Billing/MonthEndBillingModule",
    stateReportsBilling: "/Billing/StateReportsBilling",
  },

  client: {
    base: "/client",
    home: "/clienthome",
    clientUsers: "/Client/ClientUsers",
    notificationPreferences: "/Client/NotificationPreferences",
    stateReports: "/Client/StateReports",
    nonPremiumEndorsements: "/Client/NonPremiumEndorsements",
    surplusLinesTaxEstimator: "/client/SurplusLinesTaxEstimator",
    createDiligentSearchForm: "/client/CreateDiligentSearchForm",
    monthlyAccountPerformance: "/client/MonthlyAccountPerformanceReport",
  },

  filingQueue: {
    base: "/FilingQueue",
    nonPremiumEndorsement: "/FilingQueue/NonPremiumEndorsement",
    removedQueue: "/FilingQueue/RemovedQueue",
    /** SLIP+ filing review screen — opened per-row from the queue:
     *  /FilingQueue/Review/:state/:customerPolicyId  (state = "Tennessee" | "Florida") */
    filingReview: "/FilingQueue/Review",
    /** Batch filing review (Super Admin only) — client -> state -> extracted policies. */
    batchReview: "/FilingQueue/BatchReview",
    /** Client filing defaults admin (checklist #8) — per-client carrier + coverage/license per state. */
    clientDefaults: "/FilingQueue/ClientDefaults",
  },
  reconciliationQueue: "/ReconciliationQueue",

  reporting: {
    base: "/Reporting",
    openTaxReport: "/Reporting/OpenTaxReport",
    stateReport: "/Reporting/StateReport",
    yearToDate: "/Reporting/YearToDateReport",
    diligentSearch: "/Reporting/DiligentSearch",
    clientBillingReport: "/Reporting/ClientBillingReport",
    emailReportModule: "/Reporting/EmailReportModule",
    emailReportModuleDetails: "/Reporting/EmailReportModuleDetails",
    monthlyAccountPerformance: "/reporting/MonthlyAccountPerformanceReport",
    qualityControlReport: "/Reporting/QualityControlReport",
    /** Pre-rename URL for the Quality Control Report (was displayed as "No Transaction Left Behind"). */
    legacyAgedPayablesReport: "/Reporting/AgedPayablesReport",
  },
  customerOpenTaxReport: "/Customer/CustomerOpenTaxReport",

  partners: "/Partners",
  addEditPartners: "/AddEditPartners",
  userPartners: "/UserPartners",

  policy: "/Policy",
  policyDashboard: "/PolicyDashboard",
  policies: "/Policies",
  customer: "/Customer",
  customerPolicy: "/CustomerPolicy",
  createCustomerPolicy: "/CreateCustomerPolicy",
  customerPolicyDocumentData: "/CustomerPolicyDocumentData",
  addEditStateStamps: "/AddEditStateStamps",

} as const;

export type AppRouteKey = keyof typeof AppRoutes;
export type AppRouteValue = typeof AppRoutes[AppRouteKey];
